import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  useInnerBlocksProps,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { Button, Notice } from '@wordpress/components';
import { useSelect, useDispatch } from '@wordpress/data';
import { createBlock } from '@wordpress/blocks';
import AllInspectorControls from './InspectorControls';
import { getEditorStyles } from '../styles';
import '../style.scss';

const ALLOWED_BLOCKS = ['brandy/social-icon'];

/**
 * Seeded children, each with a URL.
 *
 * The URL is not optional decoration: render.php skips any icon whose URL is
 * empty (SocialIcons::get_items), so seeding bare services produced a block
 * that looked populated in the editor and rendered nothing at all on the front
 * end. Service home pages are the honest default — they resolve, and they are
 * obviously a stand-in for the site's own profile.
 */
const DEFAULT_ICONS = [
  { service: 'facebook', url: 'https://facebook.com/' },
  { service: 'instagram', url: 'https://instagram.com/' },
  { service: 'x', url: 'https://x.com/' },
];

const TEMPLATE = DEFAULT_ICONS.map((attributes) => [
  'brandy/social-icon',
  attributes,
]);

export default function Edit({ attributes, setAttributes, clientId }) {
  const { shape, colorMode, justifyContent, showLabels } = attributes;

  // A block with no children renders nothing on the front end — by design, but
  // silently, which is indistinguishable from "the block is broken". Surface it
  // here, with a one-click way to populate it: the inner-block template only
  // runs on first insert, so a block that somehow ends up empty (children
  // deleted, or inserted from a stale editor session before the child block
  // type had registered) otherwise has no recovery path at all.
  const hasIcons = useSelect(
    (select) => select(blockEditorStore).getBlocks(clientId).length > 0,
    [clientId]
  );
  const { replaceInnerBlocks } = useDispatch(blockEditorStore);

  const addDefaultIcons = () =>
    replaceInnerBlocks(
      clientId,
      DEFAULT_ICONS.map((attributes) =>
        createBlock('brandy/social-icon', attributes)
      ),
      false
    );

  const blockProps = useBlockProps({
    className: [
      'brandy-social-icons',
      `is-shape-${shape}`,
      `is-color-${colorMode}`,
      `is-justified-${justifyContent}`,
      showLabels ? 'has-labels' : '',
    ]
      .filter(Boolean)
      .join(' '),
    style: getEditorStyles(attributes),
  });

  // The list element IS the wrapper, matching render.php, so alignment and gap
  // land on the same node in both runtimes.
  const innerBlocksProps = useInnerBlocksProps(blockProps, {
    allowedBlocks: ALLOWED_BLOCKS,
    template: TEMPLATE,
    orientation: 'horizontal',
  });

  return (
    <>
      <AllInspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
      />

      {!hasIcons && (
        <Notice status="warning" isDismissible={false}>
          {__(
            'This block has no profile links yet, so it will not appear on the front end.',
            'brandy-blocks'
          )}{' '}
          <Button variant="link" onClick={addDefaultIcons}>
            {__('Add the default icons', 'brandy-blocks')}
          </Button>
        </Notice>
      )}

      <ul {...innerBlocksProps} />
    </>
  );
}
